import { useCallback, useEffect, useRef, useState } from 'react';

import {
  IconButton,
  InputAdornment,
  InputBase,
  makeStyles,
} from '@material-ui/core';

import { RiCloseLine, RiSearchLine } from '@remixicon/react';
import classNames from 'classnames';

import { useDebounced } from '../../hooks/useDebounced';
import { useFilterShortcut } from '../../hooks/useFilterShortcut';
import { SecondaryIconButton } from '../SecondaryIconButton';
import { useFilters } from './provider';

const useStyles = makeStyles({
  root: {
    backgroundColor: 'var(--background-tinted-base)',
    borderRadius: 'var(--encore-corner-radius-base)',
    fontSize: 'var(--encore-text-size-smaller)',
    height: 32,
    overflow: 'hidden',
    padding: '0 var(--encore-spacing-tighter-2)',
    width: '100%',
    '& input': {
      padding: '6px 4px 7px',
    },
  },
  rootCollapsed: {
    width: 220,
  },
  collapsedButton: {
    color: 'var(--text-subdued)',
  },
});

interface FilterSearchProps {
  searchCollapsed?: boolean;
  debounceTime?: number;
}

export const FilterSearch = ({
  searchCollapsed = false,
  debounceTime = 300,
}: FilterSearchProps) => {
  const classes = useStyles();
  const { searchQuery, setQuery } = useFilters();
  const containerRef = useRef<HTMLDivElement>(null);
  const [filterShortcutEnabled] = useFilterShortcut();
  const [expanded, setExpanded] = useState(false);

  const [displayValue, handleChangeDebounced, _handleChangeImmediately] =
    useDebounced<string>(searchQuery, setQuery, debounceTime);

  const isCollapsed = searchCollapsed && !expanded && !displayValue;

  const handleKeyDown = useCallback(
    (event: {
      key: string;
      metaKey: boolean;
      shiftKey: boolean;
      preventDefault: () => void;
    }) => {
      if (event.key !== 'f' || !event.metaKey || event.shiftKey) return;
      if (!filterShortcutEnabled) return;

      if (searchCollapsed && !expanded) {
        event.preventDefault();
        setExpanded(true);
        return;
      }

      const input = containerRef.current?.querySelector<HTMLElement>(
        'input, [role="searchbox"]',
      );
      if (!input) return;

      if (document.activeElement === input) return;

      event.preventDefault();
      input.focus();
    },
    [filterShortcutEnabled, searchCollapsed, expanded],
  );

  useEffect(() => {
    const listener = (event: KeyboardEvent) => handleKeyDown(event);
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [handleKeyDown]);

  if (isCollapsed) {
    return (
      <SecondaryIconButton
        aria-label="search"
        className={classes.collapsedButton}
        onClick={() => setExpanded(true)}
      >
        <RiSearchLine size={18} />
      </SecondaryIconButton>
    );
  }

  return (
    <InputBase
      ref={containerRef}
      className={classNames(classes.root, {
        [classes.rootCollapsed]: searchCollapsed,
      })}
      name="filter-bar"
      placeholder="Filter..."
      value={displayValue}
      autoFocus={expanded}
      onChange={e => handleChangeDebounced(e.target.value)}
      onKeyDown={handleKeyDown}
      onBlur={() => {
        if (searchCollapsed && !displayValue) setExpanded(false);
      }}
      startAdornment={
        <InputAdornment position="start">
          <RiSearchLine size={18} />
        </InputAdornment>
      }
      endAdornment={
        displayValue ? (
          <InputAdornment position="end">
            <IconButton
              size="small"
              aria-label="Clear search"
              onClick={() => _handleChangeImmediately('')}
              style={{ padding: 2 }}
            >
              <RiCloseLine size={14} />
            </IconButton>
          </InputAdornment>
        ) : undefined
      }
      inputProps={{ 'aria-label': 'search', role: 'searchbox' }}
    />
  );
};
